import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ExternalPlatform, ExternalAccount, OtpChannel } from '../types';
import { Button, Flex, Input, Text, Card } from './base-components';
import { useTranslation } from '../i18n/TranslationContext';
import { useToast } from '../contexts/ToastContext';
import { useSendOTP, useConnectWithOTP, useSendAxsOtp, useVerifyAxsOtp, useReconnectDiceSendOtp, useReconnectDiceVerifyOtp } from '../hooks/useApi';
import { trackEvent } from '../services/analytics';

const CHANNEL_LABEL: Record<OtpChannel, string> = {
  sms: 'SMS',
  email: 'Email',
  call: 'Phone call',
};

// Used only when the server didn't name a destination.
const CHANNEL_DESTINATION: Record<OtpChannel, string> = {
  sms: 'phone',
  email: 'email',
  call: 'phone',
};

// available_channels only arrives with the first send response, so the picker
// starts with all three and narrows once DICE tells us what this account has.
const DEFAULT_CHANNELS: OtpChannel[] = ['sms', 'email', 'call'];

const createDiceSchema = (t: (key: string) => string) => z.object({
  phone: z.string()
    .min(1, 'Phone number is required')
    // E.164: leading + then a country code (1-9) and 9-14 more digits.
    // Backend accepts 11-16 chars including the +, i.e. 10-15 digits total.
    .regex(/^\+[1-9]\d{9,14}$/, 'Please enter a valid phone number including country code (e.g. +44...)'),
});

const createAxsSchema = (t: (key: string) => string) => z.object({
  email: z.string().email(t('validation.emailInvalid')),
  password: z.string().min(1, t('validation.passwordRequired')),
});

const createTaoSchema = (t: (key: string) => string) => z.object({
  email: z.string().email(t('validation.emailInvalid')),
  password: z.string().min(1, t('validation.passwordRequired')),
  card: z.string().optional(),
});


interface Props {
  platform: ExternalPlatform | null;
  open: boolean;
  onClose: () => void;
  onConnectDice: (phone: string, password: string) => Promise<void>;
  onConnectTAO: (email: string, password: string, card: string) => Promise<void>;
  // When set, the modal is reconnecting an existing account rather than adding a
  // new one. For DICE we already hold the phone server-side, so we skip straight
  // to the OTP step; for AXS/TAO we prefill the known email.
  reconnectAccount?: ExternalAccount | null;
}

// Helper function to normalise phone input as the user types.
// Country-agnostic: keep a single leading + and the digits the user entered,
// without forcing any country code or US-specific grouping/truncation.
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) {
    // Preserve a lone "+" so the user can start typing a country code.
    return value.trim().startsWith('+') ? '+' : '';
  }
  return `+${digits}`;
};

// Helper function to convert the input to E.164, preserving the country code
// the user typed. Only adds the leading + when it is missing.
const toE164 = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 0 ? '' : `+${digits}`;
};

export const ConnectAccountModal: React.FC<Props> = ({
  platform,
  open,
  onClose,
  onConnectDice,
  onConnectTAO,
  reconnectAccount,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useToast();
  const prevPlatformRef = useRef<ExternalPlatform | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [otpStep, setOtpStep] = useState<'phone' | 'otp'>('phone');
  const [otpChannel, setOtpChannel] = useState<OtpChannel>('sms');
  const [otpCode, setOtpCode] = useState('');
  const [deviceId, setDeviceId] = useState('');

  // DICE reconnect: the phone lives server-side, so we auto-send the OTP using
  // the connection id and jump straight to the code-entry step.
  const isDiceReconnect = !!reconnectAccount && reconnectAccount.platform === 'DICE';
  // Where the code actually went, verbatim from the server: a full email on
  // email sends, a masked phone on sms/call. Never masked client-side.
  const [sentToDisplay, setSentToDisplay] = useState('');
  // The channel DICE actually used — it can fall back, so this beats the pick.
  const [sentChannel, setSentChannel] = useState<OtpChannel>('sms');
  // Channels this account supports, per DICE. Empty until the first send.
  const [availableChannels, setAvailableChannels] = useState<OtpChannel[]>([]);
  const reconnectDiceSendOtp = useReconnectDiceSendOtp();
  const reconnectDiceVerifyOtp = useReconnectDiceVerifyOtp();

  // AXS 2-step state. AXS uses email/password to trigger an SMS OTP, then we
  // submit the OTP back along with the mfa_request_token + login_hash issued
  // in step 1. The token + hash live only for the duration of the modal.
  const [axsStep, setAxsStep] = useState<'creds' | 'otp'>('creds');
  const [axsOtpCode, setAxsOtpCode] = useState('');
  const [axsAuthCtx, setAxsAuthCtx] = useState<{
    email: string;
    mfaRequestToken: string;
    loginHash: string;
    browserSession: boolean;
  } | null>(null);

  const diceSchema = useMemo(() => createDiceSchema(t), [t]);
  const axsSchema = useMemo(() => createAxsSchema(t), [t]);
  const taoSchema = useMemo(() => createTaoSchema(t), [t]);

  type DiceFormData = z.infer<typeof diceSchema>;
  type AxsFormData = z.infer<typeof axsSchema>;
  type TaoFormData = z.infer<typeof taoSchema>;

  const sendOTPMutation = useSendOTP();
  const connectWithOTPMutation = useConnectWithOTP();
  const sendAxsOtpMutation = useSendAxsOtp();
  const verifyAxsOtpMutation = useVerifyAxsOtp();

  const diceForm = useForm<DiceFormData>({
    resolver: zodResolver(diceSchema),
    defaultValues: { phone: '' },
  });

  const axsForm = useForm<AxsFormData>({
    resolver: zodResolver(axsSchema),
    defaultValues: { email: '', password: '' },
  });

  const taoForm = useForm<TaoFormData>({
    resolver: zodResolver(taoSchema),
    defaultValues: { email: '', password: '', card: '' },
  });

  // Reset forms when modal closes
  useEffect(() => {
    if (!open) {
      diceForm.reset();
      axsForm.reset();
      taoForm.reset();
      prevPlatformRef.current = null;
      setPhoneInput('');
      setOtpStep('phone');
      setOtpChannel('sms');
      setOtpCode('');
      setDeviceId('');
      setAxsStep('creds');
      setAxsOtpCode('');
      setAxsAuthCtx(null);
      setSentToDisplay('');
      setSentChannel('sms');
      setAvailableChannels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // DICE reconnect: let the user pick SMS vs email before we fire the OTP, so we
  // stay on the channel-selection step (handleReconnectSendOtp sends it).
  // AXS/TAO reconnect: prefill the email.
  useEffect(() => {
    if (!open || !reconnectAccount) return;
    if (reconnectAccount.platform === 'AXS' && reconnectAccount.email) {
      axsForm.setValue('email', reconnectAccount.email);
    } else if (reconnectAccount.platform === 'TAO' && reconnectAccount.email) {
      taoForm.setValue('email', reconnectAccount.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reconnectAccount]);

  // Reset form when platform changes (only when modal is open)
  useEffect(() => {
    if (open && platform && prevPlatformRef.current && prevPlatformRef.current !== platform) {
      // Platform changed - reset all forms to ensure clean state
      diceForm.reset();
      axsForm.reset();
      taoForm.reset();
      prevPlatformRef.current = platform;
    } else if (open && platform) {
      prevPlatformRef.current = platform;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, platform]);

  const isSubmitting =
    diceForm.formState.isSubmitting ||
    axsForm.formState.isSubmitting ||
    taoForm.formState.isSubmitting ||
    sendOTPMutation.isPending ||
    connectWithOTPMutation.isPending ||
    sendAxsOtpMutation.isPending ||
    verifyAxsOtpMutation.isPending ||
    reconnectDiceSendOtp.isPending ||
    reconnectDiceVerifyOtp.isPending;

  // Before the first send DICE hasn't reported this account's channels, so we
  // offer all three; afterwards we show exactly what it said it supports.
  const reconnectChannelOptions = availableChannels.length ? availableChannels : DEFAULT_CHANNELS;
  const otherChannels = reconnectChannelOptions.filter((ch) => ch !== sentChannel);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!isSubmitting && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneInput(formatted);
    // Update form with E.164 format
    const e164 = toE164(formatted);
    diceForm.setValue('phone', e164, { shouldValidate: true });
  };

  const handleSendOTP = async (data: DiceFormData) => {
    if (sendOTPMutation.isPending) return;
    console.log('Sending OTP for DICE:', data);
    try {
      const result = await sendOTPMutation.mutateAsync({
        phone: data.phone,
        platform: 'dice',
        channel: otpChannel,
      });
      console.log('OTP sent, device_id:', result.device_id);
      setDeviceId(result.device_id);
      setOtpStep('otp');
      showSuccess('OTP sent! Please check your phone.');
      trackEvent('dice_otp_sent');
    } catch (err: any) {
      console.error('OTP send error:', err);
      diceForm.setError('root', { message: err?.message || 'Failed to send OTP' });
    }
  };

  const handleVerifyOTP = async () => {
    if (connectWithOTPMutation.isPending || !otpCode || !deviceId) return;

    const phone = diceForm.getValues('phone');
    console.log('Verifying OTP for DICE:', { phone, otp: otpCode, deviceId });

    try {
      const result: any = await connectWithOTPMutation.mutateAsync({
        phone,
        otp: otpCode,
        platform: 'dice',
        deviceId,
      });
      console.log('DICE connection result:', result);
      diceForm.reset();
      setPhoneInput('');
      setOtpStep('phone');
      setOtpChannel('sms');
      setOtpCode('');
      setDeviceId('');
      onClose();
      showSuccess(result?.message || t('modals.connectAccount.success') || 'DICE account connected successfully');
      trackEvent('account_connected', { platform: 'DICE' });
    } catch (err: any) {
      console.error('DICE connection error:', err);
      diceForm.setError('root', { message: err?.message || t('modals.connectAccount.failedToConnect') });
    }
  };

  // DICE reconnect: send the OTP over the channel the user picked (SMS or email).
  // The phone/email both live server-side, so we only pass the connection id +
  // channel and surface whichever masked destination the server returns.
  const handleReconnectSendOtp = async (channel: OtpChannel = otpChannel) => {
    if (!reconnectAccount || reconnectDiceSendOtp.isPending) return;
    try {
      const result = await reconnectDiceSendOtp.mutateAsync({
        connectionId: reconnectAccount.id,
        channel,
      });
      setDeviceId(result.device_id);

      // DICE can fall back to another channel — the response says what really
      // happened, so it wins over what we asked for.
      const actualChannel = result.channel || channel;
      setSentChannel(actualChannel);
      setOtpChannel(actualChannel);

      // sent_to_display is the real destination. phone_masked is always the
      // phone regardless of channel, so it must not be shown on this screen.
      setSentToDisplay(result.sent_to_display || '');

      if (result.available_channels?.length) {
        setAvailableChannels(result.available_channels);
      }

      setOtpStep('otp');
      trackEvent('reconnect_otp_sent', { platform: 'DICE', channel: actualChannel });
    } catch (err: any) {
      diceForm.setError('root', { message: err?.message || 'Failed to send OTP' });
    }
  };

  const handleVerifyReconnectOTP = async () => {
    if (!reconnectAccount || reconnectDiceVerifyOtp.isPending || !otpCode || !deviceId) return;
    try {
      const result: any = await reconnectDiceVerifyOtp.mutateAsync({
        connectionId: reconnectAccount.id,
        otp: otpCode,
        deviceId,
      });
      setOtpCode('');
      setDeviceId('');
      onClose();
      showSuccess(result?.message || 'DICE account reconnected successfully');
      trackEvent('account_reconnected', { platform: 'DICE' });
    } catch (err: any) {
      diceForm.setError('root', { message: err?.message || t('modals.connectAccount.failedToConnect') });
    }
  };

  const handleAxsSendOtp = async (data: AxsFormData) => {
    if (sendAxsOtpMutation.isPending) return;
    try {
      const result = await sendAxsOtpMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });
      setAxsAuthCtx({
        email: result.email || data.email,
        mfaRequestToken: result.mfa_request_token,
        loginHash: result.login_hash || '',
        browserSession: !!result.browser_session,
      });
      setAxsStep('otp');
      trackEvent('axs_otp_sent');
    } catch (err: any) {
      axsForm.setError('root', { message: err?.message || t('modals.connectAccount.failedToConnect') });
    }
  };

  const handleAxsVerifyOtp = async () => {
    if (!axsAuthCtx || !axsOtpCode || verifyAxsOtpMutation.isPending) return;
    try {
      const result: any = await verifyAxsOtpMutation.mutateAsync({
        email: axsAuthCtx.email,
        otp: axsOtpCode,
        mfaRequestToken: axsAuthCtx.mfaRequestToken,
        loginHash: axsAuthCtx.loginHash,
        browserSession: axsAuthCtx.browserSession,
      });
      axsForm.reset();
      setAxsStep('creds');
      setAxsOtpCode('');
      setAxsAuthCtx(null);
      onClose();
      showSuccess(result?.message || t('modals.connectAccount.success') || 'AXS account connected successfully');
      trackEvent('account_connected', { platform: 'AXS' });
    } catch (err: any) {
      axsForm.setError('root', { message: err?.message || t('modals.connectAccount.failedToConnect') });
    }
  };

  const handleTaoSubmit = async (data: TaoFormData) => {
    try {
      const result: any = await onConnectTAO(data.email, data.password, data.card || '');
      taoForm.reset();
      onClose(); // Close modal immediately
      showSuccess(result?.message || t('modals.connectAccount.success') || 'TAO account connected successfully');
      trackEvent('account_connected', { platform: 'TAO' });
    } catch (err: any) {
      console.error('TAO connection error:', err);
      taoForm.setError('root', { message: err?.message || t('modals.connectAccount.failedToConnect') });
    }
  };

  // Don't render if modal is closed, but keep platform for smooth transitions
  if (!open) return null;
  if (!platform) return null;

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      zIndex={1000}
      onClick={handleBackdropClick}
    >
      <Card
        fullWidth
        maxWidth="md"
        shadow
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={2}>{reconnectAccount ? `Reconnect ${platform}` : t('modals.connectAccount.title', { platform })}</Text>
        <Flex direction="col" mt={3} key={platform}>
          {platform === 'DICE' && (
            <>
              {isDiceReconnect && otpStep === 'phone' ? (
                <div>
                  <Text variant="muted" mb={3}>
                    We'll send a verification code to reconnect your DICE account.
                  </Text>
                  <Flex direction="col" mb={3}>
                    <Text variant="muted" mb={1}>Send code via</Text>
                    <Flex gap="0.5rem">
                      {reconnectChannelOptions.map((ch) => (
                        <Button
                          key={ch}
                          type="button"
                          variant={otpChannel === ch ? 'primary' : 'secondary'}
                          onClick={() => setOtpChannel(ch)}
                          disabled={reconnectDiceSendOtp.isPending}
                        >
                          {ch === 'sms' && (reconnectAccount?.phone || reconnectAccount?.phone_masked)
                            ? `SMS (${reconnectAccount.phone || `···${reconnectAccount.phone_masked!.replace(/^\D+/, '')}`})`
                            : CHANNEL_LABEL[ch]}
                        </Button>
                      ))}
                    </Flex>
                  </Flex>
                  <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-blue-400 text-sm">
                      ℹ️ DICE will send a code via {CHANNEL_LABEL[otpChannel]} to verify your account
                    </p>
                  </div>
                  {diceForm.formState.errors.root && (
                    <Text variant="error" mb={2}>
                      {diceForm.formState.errors.root.message}
                    </Text>
                  )}
                  <Flex justify="end" gap="1rem">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={reconnectDiceSendOtp.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => handleReconnectSendOtp()}
                      disabled={reconnectDiceSendOtp.isPending}
                    >
                      {reconnectDiceSendOtp.isPending ? 'Sending code...' : 'Send code'}
                    </Button>
                  </Flex>
                </div>
              ) : otpStep === 'phone' ? (
                <form onSubmit={diceForm.handleSubmit(handleSendOTP)}>
                  <Flex direction="col" mb={3}>
                    <Input
                      type="tel"
                      label="Phone Number"
                      placeholder="+44 7911 123456"
                      value={phoneInput}
                      onChange={handlePhoneChange}
                      error={!!diceForm.formState.errors.phone}
                      errorMessage={diceForm.formState.errors.phone?.message}
                      disabled={sendOTPMutation.isPending}
                    />
                    <Text variant="muted" mt={1}>
                      Enter your number with country code (e.g. +1, +44, +33)
                    </Text>
                  </Flex>
                  <Flex direction="col" mb={3}>
                    <Text variant="muted" mb={1}>Send code via</Text>
                    <Flex gap="0.5rem">
                      <Button
                        type="button"
                        variant={otpChannel === 'sms' ? 'primary' : 'secondary'}
                        onClick={() => setOtpChannel('sms')}
                        disabled={sendOTPMutation.isPending}
                      >
                        SMS
                      </Button>
                      <Button
                        type="button"
                        variant={otpChannel === 'email' ? 'primary' : 'secondary'}
                        onClick={() => setOtpChannel('email')}
                        disabled={sendOTPMutation.isPending}
                      >
                        Email
                      </Button>
                    </Flex>
                  </Flex>
                  <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-blue-400 text-sm">
                      ℹ️ DICE will send an OTP via {otpChannel === 'email' ? 'email' : 'SMS'} to verify your account
                    </p>
                  </div>
                  {diceForm.formState.errors.root && (
                    <Text variant="error" mb={2}>
                      {diceForm.formState.errors.root.message}
                    </Text>
                  )}
                  <Flex justify="end" gap="1rem">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={sendOTPMutation.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={sendOTPMutation.isPending}
                    >
                      {sendOTPMutation.isPending ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </Flex>
                </form>
              ) : (
                <div>
                  <Flex direction="col" mb={3}>
                    <Input
                      type="text"
                      label="Enter OTP Code"
                      placeholder="123456"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      disabled={connectWithOTPMutation.isPending || reconnectDiceVerifyOtp.isPending}
                    />
                    <Text variant="muted" mt={1}>
                      Enter the code sent to {isDiceReconnect
                        ? (sentToDisplay || CHANNEL_DESTINATION[sentChannel])
                        : phoneInput}
                    </Text>
                  </Flex>
                  <div className="mb-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm">
                      ✓ OTP sent successfully! Check your {CHANNEL_DESTINATION[isDiceReconnect ? sentChannel : otpChannel]}.
                    </p>
                  </div>
                  {isDiceReconnect && otherChannels.length > 0 && (
                    <Flex gap="0.75rem" mb={3} align="center">
                      <Text variant="muted" size="sm">Didn't get it?</Text>
                      {otherChannels.map((ch) => (
                        <button
                          key={ch}
                          type="button"
                          onClick={() => handleReconnectSendOtp(ch)}
                          disabled={reconnectDiceSendOtp.isPending}
                          className="text-sm text-[#3b82f6] hover:underline disabled:opacity-50"
                        >
                          {reconnectDiceSendOtp.isPending ? 'Sending…' : `Send by ${CHANNEL_LABEL[ch]}`}
                        </button>
                      ))}
                    </Flex>
                  )}
                  {diceForm.formState.errors.root && (
                    <Text variant="error" mb={2}>
                      {diceForm.formState.errors.root.message}
                    </Text>
                  )}
                  <Flex justify="end" gap="1rem">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setOtpStep('phone');
                        setOtpCode('');
                        setDeviceId('');
                        diceForm.clearErrors();
                      }}
                      disabled={connectWithOTPMutation.isPending || reconnectDiceVerifyOtp.isPending}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={isDiceReconnect ? handleVerifyReconnectOTP : handleVerifyOTP}
                      disabled={(connectWithOTPMutation.isPending || reconnectDiceVerifyOtp.isPending) || !otpCode}
                    >
                      {(connectWithOTPMutation.isPending || reconnectDiceVerifyOtp.isPending) ? t('common.connecting') : (isDiceReconnect ? 'Reconnect' : t('common.connect'))}
                    </Button>
                  </Flex>
                </div>
              )}
            </>
          )}

          {platform === 'AXS' && (
            <>
              {axsStep === 'creds' ? (
                <form onSubmit={axsForm.handleSubmit(handleAxsSendOtp)}>
                  <Flex direction="col" mb={2}>
                    <Input
                      {...axsForm.register('email')}
                      type="email"
                      label={t('modals.connectAccount.email')}
                      error={!!axsForm.formState.errors.email}
                      errorMessage={axsForm.formState.errors.email?.message}
                      disabled={sendAxsOtpMutation.isPending}
                    />
                  </Flex>
                  <Flex direction="col" mb={3}>
                    <Input
                      {...axsForm.register('password')}
                      type="password"
                      label={t('modals.connectAccount.password')}
                      error={!!axsForm.formState.errors.password}
                      errorMessage={axsForm.formState.errors.password?.message}
                      disabled={sendAxsOtpMutation.isPending}
                    />
                  </Flex>
                  <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-blue-400 text-sm">
                      ℹ️ AXS will send an OTP to the phone number on your account
                    </p>
                  </div>
                  {axsForm.formState.errors.root && (
                    <Text variant="error" mb={2}>
                      {axsForm.formState.errors.root.message}
                    </Text>
                  )}
                  <Flex justify="end" gap="1rem">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={sendAxsOtpMutation.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={sendAxsOtpMutation.isPending}
                    >
                      {sendAxsOtpMutation.isPending ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </Flex>
                </form>
              ) : (
                <div>
                  <Flex direction="col" mb={3}>
                    <Input
                      type="text"
                      label="Enter OTP Code"
                      placeholder="123456"
                      value={axsOtpCode}
                      onChange={(e) => setAxsOtpCode(e.target.value)}
                      disabled={verifyAxsOtpMutation.isPending}
                    />
                    <Text variant="muted" mt={1}>
                      Enter the code AXS sent to the phone on file for {axsAuthCtx?.email}
                    </Text>
                  </Flex>
                  <div className="mb-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm">
                      ✓ OTP sent. Check your phone.
                    </p>
                  </div>
                  {axsForm.formState.errors.root && (
                    <Text variant="error" mb={2}>
                      {axsForm.formState.errors.root.message}
                    </Text>
                  )}
                  <Flex justify="end" gap="1rem">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setAxsStep('creds');
                        setAxsOtpCode('');
                        setAxsAuthCtx(null);
                        axsForm.clearErrors();
                      }}
                      disabled={verifyAxsOtpMutation.isPending}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleAxsVerifyOtp}
                      disabled={verifyAxsOtpMutation.isPending || !axsOtpCode}
                    >
                      {verifyAxsOtpMutation.isPending ? t('common.connecting') : t('common.connect')}
                    </Button>
                  </Flex>
                </div>
              )}
            </>
          )}

          {platform === 'TAO' && (
            <form onSubmit={taoForm.handleSubmit(handleTaoSubmit)}>
              <Flex direction="col" mb={2}>
                <Input
                  {...taoForm.register('email')}
                  type="email"
                  label={t('modals.connectAccount.email')}
                  error={!!taoForm.formState.errors.email}
                  errorMessage={taoForm.formState.errors.email?.message}
                />
              </Flex>
              <Flex direction="col" mb={2}>
                <Input
                  {...taoForm.register('password')}
                  type="password"
                  label={t('modals.connectAccount.password')}
                  error={!!taoForm.formState.errors.password}
                  errorMessage={taoForm.formState.errors.password?.message}
                />
              </Flex>
              <Flex direction="col" mb={3}>
                <Input
                  {...taoForm.register('card')}
                  label={t('modals.connectAccount.cardNumber')}
                  placeholder="Optional - Only if you have a physical TAO card"
                  error={!!taoForm.formState.errors.card}
                  errorMessage={taoForm.formState.errors.card?.message}
                />
                <Text variant="muted" size="xs" mt={1}>
                  Only required if you have a printed TAO card
                </Text>
              </Flex>
              {taoForm.formState.errors.root && (
                <Text variant="error" mb={2}>
                  {taoForm.formState.errors.root.message}
                </Text>
              )}
              <Flex justify="end" gap="1rem">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={taoForm.formState.isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={taoForm.formState.isSubmitting}
                >
                  {taoForm.formState.isSubmitting ? t('common.connecting') : t('common.connect')}
                </Button>
              </Flex>
        </form>
          )}
        </Flex>
      </Card>
    </Flex>
  );
};


